import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MODULE_ACCESS_DENIED_MESSAGE } from "../common/apiResponse";
import { useCommonMessage } from "../common/CommonMessage";
import {
  isFriendlyRouteRedirect,
} from "../common/permissions";
import { ThemeContext } from "../common/themeContext";
import { useAdminPermissions } from "../hooks/useAdminPermissions";
import Loader from "../components/Loder/loader";
import { hasAuthToken } from "../common/localStorage";

const AuthMiddleware = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const messageApi = useCommonMessage();
  const { brandId } = useContext(ThemeContext);
  const deniedPathRef = useRef(null);
  const profileBrandIdRef = useRef(null);
  const {
    isPending: profilePending,
    isLoading: profileLoading,
    resolveAuthorizedRoute,
    canAccessRoute,
    profileState,
    refetchProfile,
  } = useAdminPermissions();

  useEffect(() => {
    profileBrandIdRef.current = null;
  }, [brandId]);

  useEffect(() => {
    if (hasAuthToken() && brandId != null) {
      refetchProfile({ refBrandId: brandId });
    }
  }, [location.pathname, brandId, refetchProfile]);

  useEffect(() => {
    if (!profileLoading && profileState?.data != null && brandId != null) {
      profileBrandIdRef.current = brandId;
    }
  }, [brandId, profileLoading, profileState?.data]);

  const isProfileSyncedWithBrand =
    brandId == null || profileBrandIdRef.current === brandId;
  const shouldDeferPermissionCheck =
    profilePending ||
    profileLoading ||
    (profileState?.data != null && !isProfileSyncedWithBrand);

  const authorizedRoute = shouldDeferPermissionCheck
    ? null
    : resolveAuthorizedRoute(location.pathname);
  const shouldRedirect =
    !shouldDeferPermissionCheck &&
    authorizedRoute != null &&
    authorizedRoute !== location.pathname;
  const hasRouteAccess = canAccessRoute(location.pathname);
  const hasDestinationAccess = authorizedRoute
    ? canAccessRoute(authorizedRoute)
    : false;

  useEffect(() => {
    if (!shouldRedirect) {
      deniedPathRef.current = null;
      return;
    }

    if (deniedPathRef.current === location.pathname) {
      return;
    }

    deniedPathRef.current = location.pathname;

    const isFriendlyRedirect = isFriendlyRouteRedirect(
      location.pathname,
      authorizedRoute,
      profileState,
    );
    const shouldShowAccessDenied =
      !hasRouteAccess &&
      !hasDestinationAccess &&
      !isFriendlyRedirect;

    if (shouldShowAccessDenied) {
      messageApi.showActionError(MODULE_ACCESS_DENIED_MESSAGE);
    }

    navigate(authorizedRoute, { replace: true });
  }, [
    authorizedRoute,
    hasDestinationAccess,
    hasRouteAccess,
    location.pathname,
    messageApi,
    navigate,
    profileState,
    shouldRedirect,
  ]);

  if (shouldRedirect) {
    return <Loader loaderTransform="loaderTransform" />;
  }

  return children;
};

export default AuthMiddleware;
